<template>
	<div class="relative mt-4 pb-20">
		<PageTitle>{{ t('title') }}</PageTitle>
		<PagePadding class="mt-5">
			<SubtitleText>{{ t('gamemode') }}</SubtitleText>
			<BattleGamemode
				class="mt-2"
				@click="battles.setGamemode('classic')"
				:modelValue="battles.gamemode === 'classic'"
				gamemode="classic"
			/>
			<BattleGamemode
				class="mt-1"
				@click="battles.setGamemode('shared')"
				:modelValue="battles.gamemode === 'shared'"
				gamemode="shared"
			/>
			<BattleGamemode
				class="mt-1"
				@click="battles.setGamemode('jackpot')"
				:modelValue="battles.gamemode === 'jackpot'"
				gamemode="jackpot"
			/>
			<SubtitleText class="mt-4">{{ t('players') }}</SubtitleText>
			<TagGroup
				class="mt-2 *:flex-1"
				v-model="battles.selectedPlayers"
				:tags="[
					{ text: battles.gamemode === 'shared' ? '2-WAY' : '1v1' },
					{ text: '2v2', hidden: battles.gamemode === 'shared' },
					{ text: battles.gamemode === 'shared' ? '3-WAY' : '1v1v1' },
					{ text: battles.gamemode === 'shared' ? '4-WAY' : '1v1v1v1' }
				]"
			/>
			<SubtitleText class="mt-4">{{ t('cases') }}</SubtitleText>
			<Draggable
				class="mt-2 space-y-1.5"
				v-model="battles.selectedCases"
				:move="dragged"
				handle=".drag-handle"
				v-if="battles.selectedCases.length"
			>
				<BaseCard
					class="flex items-center gap-2.5 p-2.5"
					v-for="item in selectedCases"
				>
					<GrabIcon
						class="drag-handle relative h-6 w-6 text-[#505050] after:absolute after:-inset-3"
					/>
					<LazyImage
						:src="caseIcon(item.uuid)"
						alt="case"
						class="aspect-square h-21 w-21 shrink-0"
					/>
					<div class="min-w-0 flex-1 px-1.5">
						<div
							class="text-label-tertiary truncate text-xs font-medium uppercase"
						>
							{{ item.name }}
						</div>
						<div class="mt-1.5 h-5">
							<CoinPrice :price="item.price" />
						</div>
						<RiskBar class="mt-3" :value="item.risk" />
					</div>
					<div
						class="p-2.5"
						@click="removeItem(item.uuid)"
						v-hover="{ active: 'scale-95' }"
					>
						<TrashIcon class="text-label-tertiary h-5 w-5" />
					</div>
				</BaseCard>
			</Draggable>
			<div
				class="mt-4 flex w-full justify-between gap-2"
				v-if="battles.selectedCases.length"
			>
				<BaseButton
					size="md"
					color="secondary"
					class="uppercase"
					@click="battles.resetCases()"
					:disabled="!selectedCases.length"
				>
					<div>{{ t('clear') }}</div>
				</BaseButton>
				<BaseButton
					size="md"
					color="secondary"
					class="relative uppercase"
					@click="openAddCase"
				>
					<PentagonIcon class="absolute! -top-0.5 -left-4.25" active>
						<PlusIcon class="h-5 w-5" />
					</PentagonIcon>
					<div class="pl-6">{{ t('addCases') }}</div>
				</BaseButton>
			</div>
			<BaseCard
				class="mt-2.5 bg-linear-to-b from-[#222] to-[#362A24] p-0.5"
				v-hover="{ active: 'scale-95' }"
				@click="openAddCase"
				v-if="!battles.selectedCases.length"
			>
				<div
					class="relative flex flex-col items-center overflow-hidden rounded-[10px] border-2 border-black p-4"
				>
					<PentagonIcon class="shrink-0" active>
						<PlusIcon class="h-5 w-5" />
					</PentagonIcon>
					<div class="text-label-secondary mt-2 text-xs font-medium uppercase">
						{{ t('addCases') }}
					</div>
				</div>
			</BaseCard>
			<SubtitleText class="mt-7">{{ t('options') }}</SubtitleText>
			<BattleOption
				class="mt-2"
				option="crazy"
				:gamemode="battles.gamemode"
				v-model="battles.crazy"
				:disabled="battles.gamemode === 'shared'"
			/>
			<BattleOption class="mt-1" option="fast" v-model="battles.fast" />
			<BattleOption
				class="mt-1"
				option="private"
				v-model="battles.privateGame"
			/>
			<BattleOption
				class="mt-1"
				option="funding"
				v-model="battles.customFunding"
			>
				<div class="px-0.5 pb-0.5" v-if="battles.customFunding">
					<div class="rounded-[10px] bg-black p-5">
						<div class="flex items-center justify-between">
							<div>{{ t('funded') }}</div>
							<div>{{ battles.fundingPercent }}%</div>
						</div>
						<BaseSlider
							class="mt-3.5"
							:rangeMin="0"
							:rangeMax="100"
							:min="5"
							:max="100"
							:step="5"
							v-model="battles.fundingPercent"
						/>
						<div class="mt-5 flex items-center justify-between">
							<div>{{ t('costForYou') }}</div>
							<CoinPrice :price="amount" />
						</div>
						<div class="mt-3 flex items-center justify-between">
							<div>{{ t('costForOthers') }}</div>
							<CoinPrice :price="fundedCost" />
						</div>
						<div
							class="mt-5 flex items-center gap-4"
							@click.stop="battles.toggleEmptySlot()"
							v-if="user.isAdmin"
						>
							<EmptySlotIcon class="text-label-secondary h-6 w-6 shrink-0" />
							<div class="min-w-0 flex-1">
								<div
									class="text-label-primary truncate text-sm font-medium uppercase"
								>
									{{ t('leaveEmpty') }}
								</div>
								<div class="text-label-secondary mt-1 text-sm">
									{{ t('leaveEmptyDesc') }}
								</div>
							</div>
							<BaseToggle class="shrink-0" :modelValue="battles.emptySlot" />
						</div>
					</div>
				</div>
			</BattleOption>
		</PagePadding>
		<BottomActions>
			<BaseButton
				class="w-full"
				size="lg"
				color="primary"
				:loading="loading"
				@click="create()"
				:disabled="createDisabled"
			>
				<div>{{ t('create') }}</div>
				<CoinPrice :price="amount" />
			</BaseButton>
		</BottomActions>
		<AddCaseModal v-model="layout.addCaseModal" />
	</div>
</template>
<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import PagePadding from '@/components/PagePadding.vue'
import SubtitleText from '@/components/SubtitleText.vue'
import BaseCard from '@/components/BaseCard.vue'
import { computed, ref } from 'vue'
import BattleGamemode from '@/components/BattleGamemode.vue'
import TagGroup from '@/components/TagGroup.vue'
import BaseButton from '@/components/BaseButton.vue'
import PlusIcon from '@/assets/icons/plus.svg'
import AddCaseModal from '@/components/modals/AddCaseModal.vue'
import { useLayout } from '@/stores/layout'
import { useBattles } from '@/stores/battles'
import { useCases } from '@/stores/cases'
import GrabIcon from '@/assets/icons/grab.svg'
import LazyImage from '@/components/LazyImage.vue'
import CoinPrice from '@/components/CoinPrice.vue'
import RiskBar from '@/components/RiskBar.vue'
import TrashIcon from '@/assets/icons/trash.svg'
import { VueDraggableNext as Draggable } from 'vue-draggable-next'
import BattleOption from '@/components/BattleOption.vue'
import BottomActions from '@/components/BottomActions.vue'
import { client } from '@/api/orpc'
import { useUser } from '@/stores/user'
import { useRouter } from 'vue-router'
import { caseIcon } from '@/utils/urls'
import { useI18n } from 'vue-i18n'
import { WebApp } from '@grammyjs/web-app'
import BaseSlider from '@/components/BaseSlider.vue'
import EmptySlotIcon from '@/assets/icons/empty-slot.svg'
import BaseToggle from '@/components/BaseToggle.vue'
import AddCasesIcon from '@/assets/icons/add-cases.svg'
import PentagonIcon from '@/components/PentagonIcon.vue'
import { useToast } from '@/stores/toast'

const { t } = useI18n({
	messages: {
		en: {
			title: 'create battle',
			gamemode: 'Gamemode',
			players: 'Players',
			clear: 'clear',
			addCases: 'add cases',
			options: 'Options',
			create: 'create',
			cases: 'Cases',
			funded: 'Funded',
			costForYou: 'Cost for you',
			costForOthers: 'Cost for others',
			leaveEmpty: 'Leave an empty slot',
			leaveEmptyDesc: 'Don`t join the battle',
			maintenance:
				'Battle creation is temporarily unavailable due to maintenance.'
		},
		ru: {
			title: 'создать батл',
			gamemode: 'Режим',
			players: 'Игроки',
			clear: 'очистить',
			addCases: 'добавить кейсы',
			options: 'Опции',
			create: 'создать',
			cases: 'Кейсы',
			funded: 'Скидка',
			costForYou: 'Цена для тебя',
			costForOthers: 'Цена для других',
			leaveEmpty: 'Оставить пустой слот',
			leaveEmptyDesc: 'Не участвовать в батле',
			maintenance:
				'Создание батлов временно недоступно из-за технических работ.'
		}
	}
})

const cases = useCases()
const battles = useBattles()

const layout = useLayout()

layout.hideBottomNavbar()

const openAddCase = () => {
	layout.openAddCaseModal()
}

const selectedCases = computed(() =>
	battles.selectedCases
		.map((uuid) => cases.list.find((c) => c.uuid === uuid))
		.filter((c) => !!c)
)

const removeItem = (uuid: string) => {
	battles.removeCaseFromSelection(uuid)
}

const price = computed(() =>
	selectedCases.value.reduce((acc, item) => {
		return acc + item.price
	}, 0)
)

const amount = computed(() => {
	let players = 0
	if (battles.selectedPlayers === 0) {
		players = 2
	} else if (battles.selectedPlayers === 1) {
		players = 4
	} else if (battles.selectedPlayers === 2) {
		players = 3
	} else if (battles.selectedPlayers === 3) {
		players = 4
	}
	const funding = battles.customFunding
		? Math.ceil(
				price.value *
					(battles.fundingPercent / 100) *
					(players - (battles.emptySlot ? 0 : 1))
			)
		: 0

	return (battles.emptySlot ? 0 : price.value) + funding
})

const user = useUser()
const router = useRouter()

const loading = ref(false)

const createDisabled = computed(() => !selectedCases.value.length)

const toast = useToast()

const create = async () => {
	if (!selectedCases.value.length) {
		return
	}
	if (amount.value > user.displayedCoins) {
		return layout.showDepositModal(
			Math.ceil(amount.value - user.displayedCoins)
		)
	}

	const modifiers: ('crazy' | 'fast')[] = []

	if (battles.crazy && battles.gamemode !== 'shared') {
		modifiers.push('crazy')
	}
	if (battles.fast) {
		modifiers.push('fast')
	}

	let teams = 0
	let teamPlayers = 0
	if (battles.selectedPlayers === 0) {
		teamPlayers = 1
		teams = 2
	} else if (battles.selectedPlayers === 1) {
		teamPlayers = 2
		teams = 2
	} else if (battles.selectedPlayers === 2) {
		teamPlayers = 1
		teams = 3
	} else if (battles.selectedPlayers === 3) {
		teamPlayers = 1
		teams = 4
	}

	loading.value = true

	const { error, data, isDefined } = await client.battle.createBattle({
		cases: battles.selectedCases
			.map((uuid) => cases.list.find((c) => c.uuid === uuid))
			.filter((c) => !!c)
			.map((c) => ({ uuid: c.uuid, version: c.version })),
		gameMode: battles.gamemode,
		modifiers,
		isPrivate: battles.privateGame,
		teams,
		teamPlayers,
		fundingPercent: battles.customFunding ? battles.fundingPercent : undefined,
		leaveEmpty: battles.customFunding ? battles.emptySlot : undefined
	})

	loading.value = false

	if (isDefined) {
		if (error.code === 'MAINTENANCE') {
			toast.add({ type: 'info', text: t('maintenance') })
		}
		if (error.code === 'NOT_ENOUGH_COINS') {
			layout.showDepositModal(Math.ceil(amount.value - user.displayedCoins))
		}

		return
	}

	if (error) {
		router.replace('/')
		console.error(error)
		return
	}

	if (!data) {
		return
	}

	user.setUpdated(
		data.updatedUser.coins,
		data.updatedUser.rewards.level,
		data.updatedUser.rewards.xp,
		data.updatedUser.rewards.levelExpSize,
		data.updatedUser.rewards.levelPercent
	)

	router.replace(`/battle/${data.battleUuid}`)

	loading.value = false
}

const dragged = () => {
	WebApp.HapticFeedback.impactOccurred('soft')
}

const fundedCost = computed(() =>
	Math.ceil((price.value * (100 - battles.fundingPercent)) / 100)
)
</script>
